/**
 * Shared helpers for the four task list views (TasksCN_Index, ProjectTasksCN,
 * PortfolioTasksCN, LocationTasksCN) and the SortableTasksTableBody component
 * that powers their drag-and-drop reorder.
 *
 * Kept dependency-free (no moment / dayjs) so it's cheap to import everywhere.
 */

export interface TaskLike {
	_id: string
	dateDue?: string | Date | null
	hasScheduledTime?: boolean
	[key: string]: any
}

/**
 * Render a due date as 'Today' / 'Tomorrow' / 'Apr 5'. Used for both the dueDate
 * cell and the day-group headers above each SortableContext.
 */
export function formatDueDate(dateStr: string | Date | null | undefined): string {
	if (!dateStr) return '-'
	const d = new Date(dateStr)
	const taskDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	const tomorrow = new Date(today)
	tomorrow.setDate(tomorrow.getDate() + 1)

	if (taskDate.getTime() === today.getTime()) return 'Today'
	if (taskDate.getTime() === tomorrow.getTime()) return 'Tomorrow'
	return taskDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Whether a due date is past: its calendar day (the UTC date, which is how a
 * date-only due date is stored, at 12:00 UTC on its day) is before the
 * viewer's today. The one copy on the client; the Tasks screens all use it.
 *
 * The server's rule (backend/utilities/taskOverdue.js, MYD-522) reads the day
 * the same way, against today in the app's time zone (Pacific by default), so
 * reports, slides and Explorer agree with these screens for Pacific viewers.
 */
export function isOverdue(dateStr: string | Date | null | undefined): boolean {
	if (!dateStr) return false
	const d = new Date(dateStr)
	const taskDate = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
	const today = new Date()
	today.setHours(0, 0, 0, 0)
	return taskDate < today
}

/**
 * Render a time-of-day from a stored Date as locale-formatted clock text
 * (e.g. '10:30 AM'). Only meaningful when the task's `hasScheduledTime` is true.
 */
export function formatScheduledTime(dateStr: string | Date | null | undefined): string {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

/**
 * Stable per-day key (YYYY-MM-DD in UTC) used to bucket tasks. Aligns with the
 * controller's `(dateDue, order)` index, which is based on the date portion of
 * dateDue in UTC.
 */
export function getDayKey(dateStr: string | Date | null | undefined): string {
	if (!dateStr) return 'undated'
	const d = new Date(dateStr)
	const y = d.getUTCFullYear()
	const m = String(d.getUTCMonth() + 1).padStart(2, '0')
	const day = String(d.getUTCDate()).padStart(2, '0')
	return `${y}-${m}-${day}`
}

/**
 * Group a sorted list of tasks by day. Returns an array of {dayKey, dayLabel,
 * tasks} preserving the input order across days. Within each group, tasks
 * retain their input order (the caller is expected to have sorted them by
 * dateDue ascending then `order` ascending, matching the backend sort).
 */
export function groupTasksByDay<T extends TaskLike>(
	tasks: T[],
): { dayKey: string; dayLabel: string; tasks: T[] }[] {
	const groups: { dayKey: string; dayLabel: string; tasks: T[] }[] = []
	const indexByKey = new Map<string, number>()
	for (const task of tasks) {
		const dayKey = getDayKey(task.dateDue)
		let idx = indexByKey.get(dayKey)
		if (idx === undefined) {
			idx = groups.length
			indexByKey.set(dayKey, idx)
			groups.push({
				dayKey,
				dayLabel: dayKey === 'undated' ? 'No due date' : formatDueDate(task.dateDue),
				tasks: [],
			})
		}
		groups[idx].tasks.push(task)
	}
	return groups
}

export type TaskSortOrder = 'asc' | 'desc'

/**
 * The comparable value for a task under a given sort column.
 *
 * Returns null for "no value". `sortTasks` always sinks nulls, in both
 * directions: an unassigned task or one with no due date is missing
 * information rather than an extreme, so reversing the sort should not float it
 * to the top.
 *
 * `assignedTo` and `dateDue` need derived values, which is why the task lists
 * cannot use a generic `a[key]` comparison for them. `assignedTo` is a
 * populated user object, so a naive comparison stringifies to '[object Object]'
 * and every row ties.
 */
export function taskSortValue(task: TaskLike, columnId: string): string | null {
	switch (columnId) {
		case 'assignedTo': {
			// Sort on what the row actually displays, which is firstName with a
			// fallback to name.
			const who = task.assignedTo?.firstName || task.assignedTo?.name || ''
			return who ? who.toLowerCase() : null
		}
		case 'taskPriority':
			// 'P1 - Urgent' .. 'P4 - Low' sort lexically into priority order.
			return task.taskPriority || null
		case 'dateDue': {
			const key = getDayKey(task.dateDue)
			return key === 'undated' ? null : key
		}
		default: {
			const raw = task[columnId]
			return raw === null || raw === undefined || raw === '' ? null : String(raw).toLowerCase()
		}
	}
}

/**
 * Returns a new array sorted by `columnId`. A null `columnId` leaves the
 * incoming order untouched, which is what keeps an unsorted list in whatever
 * order the server returned.
 */
export function sortTasks<T extends TaskLike>(
	tasks: T[],
	columnId: string | null,
	order: TaskSortOrder,
): T[] {
	if (!columnId) return tasks
	const dir = order === 'asc' ? 1 : -1
	return [...tasks].sort((a, b) => {
		const aVal = taskSortValue(a, columnId)
		const bVal = taskSortValue(b, columnId)
		if (aVal === null && bVal === null) return 0
		if (aVal === null) return 1
		if (bVal === null) return -1
		if (aVal === bVal) return 0
		return aVal < bVal ? -dir : dir
	})
}

/**
 * The list-view sort used by every task table.
 *
 * Sorting by Due Date (the default) buckets tasks by calendar day and falls
 * back to the manual `order` field within a day, so a drag-and-drop reorder
 * survives a re-sort and optimistic reorder updates render immediately. Every
 * other column defers to `sortTasks`, which sinks empty values in both
 * directions.
 */
export function sortTaskList<T extends TaskLike>(
	tasks: T[],
	columnId: string | null,
	order: TaskSortOrder,
): T[] {
	if (columnId !== 'dateDue') return sortTasks(tasks, columnId, order)

	return [...tasks].sort((a, b) => {
		const aKey = taskSortValue(a, 'dateDue')
		const bKey = taskSortValue(b, 'dateDue')
		// Undated tasks are missing information, not an extreme, so they sink to
		// the end whichever way the column is pointed.
		if (aKey === null && bKey === null) return (a.order || 0) - (b.order || 0)
		if (aKey === null) return 1
		if (bKey === null) return -1
		if (aKey !== bKey) return order === 'asc' ? aKey.localeCompare(bKey) : bKey.localeCompare(aKey)
		return (a.order || 0) - (b.order || 0)
	})
}

export interface TaskFilterState {
	searchQuery?: string
	filterPriority?: string
	/** Project `_id`. Only meaningful on lists spanning more than one project. */
	filterProject?: string
	/** Assignee user `_id`, or the `UNASSIGNED_FILTER_VALUE` sentinel. */
	filterAssignee?: string
	showCompleted?: boolean
}

/**
 * Sentinel for the "Unassigned" option. An empty string already means "no
 * filter", so tasks with no assignee need a value of their own to select.
 */
export const UNASSIGNED_FILTER_VALUE = '__unassigned__'

/** Read an id whether the field arrived populated or as a raw id string. */
function refId(value: any): string {
	if (!value) return ''
	return typeof value === 'object' ? String(value._id || '') : String(value)
}

/**
 * The filter pass shared by every task table. Each clause is skipped when its
 * control is at its default, so passing `{}` returns the list untouched apart
 * from hiding completed tasks.
 */
export function filterTasks<T extends TaskLike>(tasks: T[], filters: TaskFilterState): T[] {
	let out = tasks

	const q = (filters.searchQuery || '').trim().toLowerCase()
	if (q) {
		out = out.filter(
			t => (t.taskName || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q),
		)
	}

	if (filters.filterPriority) {
		out = out.filter(t => t.taskPriority === filters.filterPriority)
	}

	if (filters.filterProject) {
		out = out.filter(t => refId(t.projectid) === filters.filterProject)
	}

	if (filters.filterAssignee) {
		out =
			filters.filterAssignee === UNASSIGNED_FILTER_VALUE
				? out.filter(t => !refId(t.assignedTo))
				: out.filter(t => refId(t.assignedTo) === filters.filterAssignee)
	}

	if (!filters.showCompleted) {
		out = out.filter(t => !t.completed)
	}

	return out
}

/**
 * The distinct assignees present in a task list, for the Assigned filter's
 * options. Derived from the tasks themselves rather than fetched, so the
 * dropdown only ever offers values that can actually match something. An
 * `UNASSIGNED_FILTER_VALUE` entry is appended when some task has no assignee.
 */
export function collectAssignees<T extends TaskLike>(tasks: T[]): { id: string; label: string }[] {
	const byId = new Map<string, string>()
	let hasUnassigned = false

	for (const task of tasks) {
		const id = refId(task.assignedTo)
		if (!id) {
			hasUnassigned = true
			continue
		}
		if (byId.has(id)) continue
		const who = task.assignedTo
		byId.set(id, (typeof who === 'object' && (who.name || who.firstName)) || 'Unknown')
	}

	const options = [...byId.entries()]
		.map(([id, label]) => ({ id, label }))
		.sort((a, b) => a.label.localeCompare(b.label))

	if (hasUnassigned) options.push({ id: UNASSIGNED_FILTER_VALUE, label: 'Unassigned' })
	return options
}

/**
 * The distinct projects present in a task list, for the Project filter's
 * options. Only useful on lists that span more than one project. Tasks with no
 * project (portfolio-level tasks) contribute nothing — selecting any project
 * filters them out, which is the correct reading.
 */
export function collectProjects<T extends TaskLike>(tasks: T[]): { id: string; label: string }[] {
	const byId = new Map<string, string>()

	for (const task of tasks) {
		const project = task.projectid
		if (!project || typeof project !== 'object' || !project._id) continue
		const id = String(project._id)
		if (byId.has(id)) continue
		const assetName = project.projectAsset?.assetName
		byId.set(id, assetName ? `${project.projectName} - ${assetName}` : project.projectName || 'Untitled project')
	}

	return [...byId.entries()]
		.map(([id, label]) => ({ id, label }))
		.sort((a, b) => a.label.localeCompare(b.label))
}
